import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Award, ExternalLink } from "lucide-react";
import { useGetStarted } from "@/components/GetStartedDialog";
import PageHead from "@/components/PageHead";

export default function PortfolioDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [nextItem, setNextItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { open } = useGetStarted();

  useEffect(() => {
    let mounted = true;
    setItem(null);
    setNextItem(null);
    setNotFound(false);
    api
      .get(`/portfolio/${id}`)
      .then((r) => {
        if (!mounted) return;
        setItem(r.data);
        if (r.data.next_id) {
          api.get(`/portfolio/${r.data.next_id}`).then((n) => mounted && setNextItem(n.data)).catch(() => {});
        }
      })
      .catch(() => mounted && setNotFound(true));
    return () => { mounted = false; };
  }, [id]);

  if (notFound) {
    return (
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 py-32" data-testid="portfolio-not-found">
        <p className="eyebrow">404</p>
        <h1 className="font-display text-5xl mt-4">Project not found.</h1>
        <Link to="/work" className="inline-block mt-8 font-ui link-underline">
          ← Back to work
        </Link>
      </section>
    );
  }

  if (!item) {
    return (
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-32 eyebrow" data-testid="portfolio-detail-loading">
        Loading…
      </div>
    );
  }

  return (
    <article data-testid="portfolio-detail">
      <PageHead
        title={`${item.title} Case Study — Redkey Web Design`}
        description={item.tagline}
        canonical={`https://redkeyweb.design/work/${item.id}`}
      />
      {/* ---------- Header ---------- */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-14 md:pt-20">
        <Link to="/work" className="eyebrow link-underline" data-testid="detail-back">
          ← All work
        </Link>
        <span className="eyebrow ml-3">/ {item.category}</span>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <p className="eyebrow">Case Study · {item.number}</p>
            <h1 className="font-display text-5xl md:text-7xl mt-4">{item.title}</h1>
            <p className="mt-6 text-lg md:text-xl text-ink-soft leading-relaxed max-w-[52ch]">
              {item.tagline}
            </p>
            {item.award && (
              <span
                data-testid="detail-award"
                className="mt-6 inline-flex items-center gap-2 border border-brand-red text-brand-red px-3 py-1.5 font-ui text-[11px] tracking-[0.16em] uppercase"
              >
                <Award size={12} strokeWidth={2} />
                {item.award}
              </span>
            )}
          </div>

          <aside className="md:col-span-4 space-y-6 md:pt-2 font-body">
            <div>
              <p className="eyebrow">Client</p>
              <p className="mt-1">{item.client}</p>
            </div>
            <div>
              <p className="eyebrow">Role</p>
              <p className="mt-1">{item.role}</p>
            </div>
            <div>
              <p className="eyebrow">Year</p>
              <p className="mt-1">{item.year}</p>
            </div>
            <div>
              <p className="eyebrow">Visit</p>
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-external-link"
                className="mt-1 inline-flex items-center gap-2 link-underline font-ui"
              >
                {item.domain} <ExternalLink size={13} />
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* ---------- Cover ---------- */}
      <section className="mx-auto max-w-[1600px] px-2 md:px-6 mt-14">
        <div className="aspect-[16/10] overflow-hidden bg-paper-alt border hairline">
          <img
            src={item.image}
            alt={`${item.title} — main screenshot`}
            width="1600"
            height="1000"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* ---------- Metrics ---------- */}
      {item.metrics?.length > 0 && (
        <section className="mx-auto max-w-[1360px] px-6 md:px-10 mt-16 md:mt-20" data-testid="detail-metrics">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-8 border-y hairline py-10 md:py-14">
            {item.metrics.map((m, i) => (
              <div key={`${m.label}-${i}`}>
                <p className="font-display text-4xl md:text-6xl leading-none">{m.value}</p>
                <p className="mt-3 font-ui text-sm">{m.label}</p>
                {m.detail && <p className="mt-1 text-sm text-muted-ink font-body">{m.detail}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------- Problem ---------- */}
      {item.problem && (
        <section className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10" data-testid="detail-problem">
          <div className="md:col-span-4">
            <p className="eyebrow">§ Challenge</p>
            <h2 className="font-display text-3xl md:text-4xl mt-4">The problem</h2>
          </div>
          <p className="md:col-span-8 text-lg text-ink-soft leading-relaxed max-w-prose">
            {item.problem}
          </p>
        </section>
      )}

      {/* ---------- Approach ---------- */}
      {item.approach && (
        <section className="border-t hairline bg-paper-alt" data-testid="detail-approach">
          <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="eyebrow">§ Approach</p>
              <h2 className="font-display text-3xl md:text-4xl mt-4">What we did</h2>
            </div>
            <p className="md:col-span-8 text-lg text-ink-soft leading-relaxed max-w-prose">
              {item.approach}
            </p>
          </div>
        </section>
      )}

      {/* ---------- Process ---------- */}
      {item.process?.length > 0 && (
        <section className="border-t hairline" data-testid="detail-process">
          <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24">
            <p className="eyebrow">§ Process</p>
            <h2 className="font-display text-3xl md:text-5xl mt-4 mb-12">
              From discovery to launch.
            </h2>
            <ol className="space-y-0">
              {item.process.map((p) => (
                <li
                  key={p.step}
                  className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-8 border-t hairline items-start"
                >
                  <div className="md:col-span-2 font-ui text-sm text-muted-ink tracking-widest">
                    {p.step}
                  </div>
                  <h3 className="md:col-span-4 font-display text-2xl md:text-3xl">
                    {p.title}
                  </h3>
                  <p className="md:col-span-6 text-ink-soft text-lg leading-relaxed max-w-prose">
                    {p.body}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* ---------- Stack + Accessibility ---------- */}
      <section className="border-t hairline bg-paper-alt">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          {item.stack?.length > 0 && (
            <div className="md:col-span-5" data-testid="detail-stack">
              <p className="eyebrow">§ Stack</p>
              <h3 className="font-display text-2xl md:text-3xl mt-4">Built with</h3>
              <ul className="flex flex-wrap gap-2 mt-6">
                {item.stack.map((s) => (
                  <li
                    key={s}
                    className="font-ui text-sm border hairline-strong px-3 py-1.5"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {item.accessibility && (
            <div className="md:col-span-7" data-testid="detail-accessibility">
              <p className="eyebrow">§ Accessibility</p>
              <h3 className="font-display text-2xl md:text-3xl mt-4">
                Built for everyone.
              </h3>
              <p className="mt-4 text-ink-soft text-lg leading-relaxed max-w-prose">
                {item.accessibility}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ---------- Next + CTA ---------- */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          {nextItem && (
            <Link
              to={`/work/${nextItem.id}`}
              data-testid="detail-next"
              className="md:col-span-7 group block"
            >
              <p className="eyebrow">Next project</p>
              <p className="mt-3 font-display text-3xl md:text-5xl group-hover:text-electric transition-colors">
                {nextItem.title} <span aria-hidden>→</span>
              </p>
            </Link>
          )}
          <div className="md:col-span-5 md:text-right">
            <button
              type="button"
              onClick={open}
              data-testid="detail-cta"
              className="inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 font-ui"
            >
              Get Started <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      </section>
    </article>
  );
}
